import React, { useState } from 'react';
import FirebaseService from '../services/FirebaseService';
import PersonnelDetails from '../components/personnel/PersonnelDetails';

const VerifyPersonnel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('certificate'); // 'certificate' or 'name'
  const [loading, setLoading] = useState(false);
  const [personnel, setPersonnel] = useState(null);
  const [multipleResults, setMultipleResults] = useState([]);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  const handleTabChange = (newType) => {
    setSearchType(newType);
    setPersonnel(null);
    setMultipleResults([]);
    setError('');
    setSearchTerm('');
    setSearched(false);
  };

  const handleSearch = async () => {
    const trimmedSearchTerm = searchTerm.trim();
    
    if (!trimmedSearchTerm) {
      setError('Please enter a search term');
      return;
    }
    
    // Require at least 3 characters for search to prevent data exposure
    if (trimmedSearchTerm.length < 3) {
      setError('Please enter at least 3 characters for search');
      return;
    }

    setLoading(true);
    setError('');
    setPersonnel(null);
    setMultipleResults([]);
    setSearched(true);

    try {
      if (searchType === 'certificate') {
        // Search by certificate number
        const result = await FirebaseService.getPersonnelByCertificateNumber(searchTerm);
        if (result) {
          setPersonnel(result);
        } else {
          setError('No personnel found with this certificate number');
        }
      } else {
        // Search by auditor name
        const results = await FirebaseService.searchPersonnelByName(searchTerm);
        if (results && results.length > 0) {
          if (results.length === 1) {
            setPersonnel(results[0]);
          } else {
            setMultipleResults(results);
          }
        } else {
          setError('No personnel found with this name');
        }
      }
    } catch (err) {
      // Error searching personnel
      setError('An error occurred while searching. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPersonnel = (selectedPersonnel) => {
    setPersonnel(selectedPersonnel);
    setMultipleResults([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const tabs = [
    { key: 'certificate', label: 'Search by Certificate Number' },
    { key: 'name', label: 'Search by Auditor Name' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '0 16px 40px',
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    }}>
      {/* Main Card */}
      <div style={{
        width: '100%', maxWidth: '820px', backgroundColor: '#ffffff',
      }}>
        <div style={{ padding: '32px 0 0' }}>
          {/* Segmented toggle */}
          <div style={{
            display: 'inline-flex',
            backgroundColor: '#f0f2f5',
            borderRadius: '10px',
            padding: '4px',
            marginBottom: '28px',
            gap: '2px',
          }}>
            {[
              { key: 'certificate', label: 'Certificate No.', icon: '🔎' },
              { key: 'name', label: 'Auditor Name', icon: '👤' },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                style={{
                  background: searchType === key ? '#ffffff' : 'transparent',
                  border: 'none',
                  borderRadius: '7px',
                  color: searchType === key ? '#0d2347' : '#8a95a3',
                  fontSize: '13.5px',
                  fontWeight: searchType === key ? '600' : '400',
                  padding: '9px 20px',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.18s',
                  boxShadow: searchType === key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: '13px' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>

          {/* Search Row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
            <input
              type="text"
              placeholder={searchType === 'certificate' ? 'Enter Certificate Number' : 'Enter Auditor Name'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              style={{
                flex: 1, height: '52px', border: '1.5px solid #dde2ea', borderRadius: '8px',
                padding: '0 18px', fontSize: '15px', outline: 'none', color: '#0a1628',
                backgroundColor: '#f7f9fc', boxSizing: 'border-box', transition: 'border-color 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = '#0d2347'}
              onBlur={e => e.target.style.borderColor = '#dde2ea'}
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              style={{
                height: '52px', minWidth: '130px',
                background: loading ? '#8a95a3' : 'linear-gradient(135deg, #0d2347 0%, #1a3a6e 100%)',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px', padding: '0 24px',
                boxShadow: loading ? 'none' : '0 4px 14px rgba(13,35,71,0.35)', transition: 'all 0.2s',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Error */}
          {error && (
            <p style={{ color: '#c0392b', fontSize: '13.5px', margin: '12px 0 0 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </p>
          )}

          {/* Multiple Results Selector */}
          {multipleResults.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <p style={{ fontSize: '13px', color: '#8a95a3', marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Multiple results — select one:
              </p>
              <div style={{ border: '1.5px solid #dde2ea', borderRadius: '8px', overflow: 'hidden' }}>
                {multipleResults.map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => handleSelectPersonnel(item)}
                    style={{
                      padding: '14px 18px', cursor: 'pointer',
                      borderBottom: index < multipleResults.length - 1 ? '1px solid #e8ecf0' : 'none',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f7f9fc'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#0a1628', marginBottom: '2px' }}>
                      {item.auditor_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#8a95a3' }}>
                      Certificate: {item.certi_no} · Country: {item.country || 'N/A'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Personnel Result */}
          {personnel && <PersonnelDetails personnel={personnel} />}

          {/* No result fallback */}
          {searched && !personnel && !error && !loading && multipleResults.length === 0 && (
            <p style={{ color: '#8a95a3', fontSize: '14px', margin: '20px 0 0 0', textAlign: 'center' }}>
              No personnel found. Please check your search criteria and try again.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyPersonnel;
